import { Box, Coffee, CupSoda, Pizza, Soup } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import i18n from '@/utils/i18n';
import type { GetAllContainerTypeGroupsQuery } from '../../../graphql/queries/getAllContainerTypeGroups.generated';

/** A single group row as returned by the colugo GetAllContainerTypeGroups query. */
export type ContainerTypeGroup =
  GetAllContainerTypeGroupsQuery['getAllContainerTypeGroups'][number];

export type GroupMember = ContainerTypeGroup['container_types'][number];

/** Raw category strings as stored/served by colugo. `null` = no category. */
export type GroupCategory = 'Bowls' | 'Cups' | null;

/** Toolbar category filter values ('none' == the "No category" segment). */
export type CategoryFilter = 'all' | 'Bowls' | 'Cups' | 'none';

/**
 * Editable draft for the create/edit sheet. `category` mirrors the raw colugo
 * value (`null` when "No category").
 */
export type GroupDraft = {
  name: string;
  category: GroupCategory;
};

/** i18n label for a raw category value (used in badges, selects, chips). */
export function categoryLabel(category: GroupCategory): string {
  if (category === 'Bowls')
    return i18n.t('EditContainerTypeGroups:categoryBowls');
  if (category === 'Cups')
    return i18n.t('EditContainerTypeGroups:categoryCups');
  return i18n.t('EditContainerTypeGroups:noCategory');
}

/**
 * Fallback glyph for a member thumbnail when the container type has no image.
 * Real member data carries no per-item icon, so we derive it from the group's
 * category (mirrors the handoff's Soup / Coffee / Box glyph language).
 */
export function memberGlyph(category: GroupCategory): LucideIcon {
  if (category === 'Bowls') return Soup;
  if (category === 'Cups') return Coffee;
  return Box;
}

/** Extra glyphs referenced by the handoff, kept available for future categories. */
export const CATEGORY_GLYPHS = { Soup, Coffee, Box, Pizza, CupSoda };

/** A group can only be deleted with no linked stores and no assigned types. */
export function isDeleteGuarded(group: ContainerTypeGroup): boolean {
  return group.has_linked_stores || group.container_types.length > 0;
}

/** Client-side search (group name + member names) and category filtering. */
export function filterGroups(
  groups: ContainerTypeGroup[],
  search: string,
  categoryFilter: CategoryFilter,
): ContainerTypeGroup[] {
  const q = search.trim().toLowerCase();
  return groups.filter((group) => {
    const matchesQuery =
      q === '' ||
      (group.name ?? '').toLowerCase().includes(q) ||
      group.container_types.some((ct) =>
        (ct.name ?? '').toLowerCase().includes(q),
      );
    const matchesCategory =
      categoryFilter === 'all' ||
      (categoryFilter === 'none'
        ? !group.category
        : group.category === categoryFilter);
    return matchesQuery && matchesCategory;
  });
}

/** Options for the category select / segmented control (create + edit sheet). */
export function categoryOptions(): { value: string; label: string }[] {
  return [
    { value: 'none', label: i18n.t('EditContainerTypeGroups:noCategory') },
    { value: 'Bowls', label: i18n.t('EditContainerTypeGroups:categoryBowls') },
    { value: 'Cups', label: i18n.t('EditContainerTypeGroups:categoryCups') },
  ];
}

/** Whether an edit/create draft differs from the persisted group. */
export function isDraftDirty(
  draft: GroupDraft,
  original: ContainerTypeGroup | null,
): boolean {
  if (original == null) {
    return draft.name.trim() !== '' || draft.category !== null;
  }
  return (
    draft.name !== (original.name ?? '') ||
    draft.category !== ((original.category as GroupCategory) ?? null)
  );
}
